import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "~/lib/utils";
import {
  PanelPosition,
  useResizablePanelWidth,
} from "~/pages/manifold-nextgen/hooks/useResizablePanelWidth";

const MIN_WIDTH = 360;
const MAX_WIDTH = 840;
const DEFAULT_WIDTH = 420;

interface ResizableSidePanelProps extends HTMLAttributes<HTMLDivElement> {
  // Animates the panel shut without unmounting what it holds.
  collapsed?: boolean;
  // Fills the row instead, with no resize handle, e.g. for a full-screen app.
  expanded?: boolean;
  children: ReactNode;
}

/** A right-hand side panel the user can widen by dragging its left edge, as
 *  the session context sidebar and the Artifacts page's lineage panel do.
 *
 *  Pass a role and label to name the region, e.g. `role="complementary"`.
 */
export const ResizableSidePanel = ({
  collapsed = false,
  expanded = false,
  children,
  className,
  ...regionProps
}: ResizableSidePanelProps) => {
  const { width, handleDragStart, contentRef } = useResizablePanelWidth({
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    initialWidth: DEFAULT_WIDTH,
    position: PanelPosition.RIGHT,
  });

  return (
    <div
      role="region"
      {...regionProps}
      aria-hidden={collapsed || undefined}
      inert={collapsed}
      className={cn(
        "relative flex justify-end overflow-hidden bg-background",
        expanded ? "flex-1" : "shrink-0 transition-[width] duration-300 ease-in-out",
        className
      )}
      style={expanded ? undefined : { width: collapsed ? 0 : width }}
    >
      {/* A direct child of the panel: the drag resizes this handle's parent. */}
      {!expanded && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          onMouseDown={handleDragStart}
          className="group absolute inset-y-0 left-0 z-20 w-1.5 cursor-col-resize"
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 w-px bg-border transition-all group-hover:w-0.5 group-hover:bg-primary group-active:w-0.5 group-active:bg-primary" />
        </div>
      )}

      {/* Fixed to the panel's un-collapsed width so text reflows only when the
          user resizes it — the outer region's width is what animates on
          collapse, clipping this via overflow-hidden instead of squeezing it. */}
      <div
        ref={contentRef}
        className={cn("flex h-full shrink-0 flex-col overflow-hidden", expanded && "w-full")}
        style={expanded ? undefined : { width }}
      >
        {children}
      </div>
    </div>
  );
};
